import { formatDate } from '@luxuryescapes/lib-refunds';

import {
  CANCELLATION_POLICY_DAYS,
  CHANGE_OF_MIND_BEFORE_CHECK_IN_DAYS,
  FLASH_CANCELLATION_OPTION_POLICIES,
  FOURTEEN_DAYS_BEFORE_CHECK_IN,
} from '~/consts/cancellationPolicy';
import { OFFER_TYPE_HOTEL, OFFER_TYPE_RENTAL, OFFER_TYPE_TACTICAL_AO_HOTEL } from '~/consts/offerTypes';

import { addDays, formatDateShortDD, isBefore, minDate, offsetUTC, subDays } from '~/services/TimeService';

export function getCancellationPolicyLabel(cancellationPolicy: string) {
  return cancellationPolicy == 'partner-policy-prior-to-check-in-twenty-one-days'
    ? 'flexible-cancellation-twenty-one-days'
    : cancellationPolicy;
}

// Accepted by svc-reservation but not launched yet: hidden from rate plan forms in production
const UNRELEASED_CANCELLATION_POLICIES = ['cash-refund-prior-to-check-in-ten-days'];

export function getSelectableCancellationPolicies(
  cancellationPolicies: Array<string>,
  currentCancellationPolicy?: string,
  appEnv = window.configs?.APP_ENV,
): Array<string> {
  if (appEnv !== 'production') {
    return cancellationPolicies;
  }
  return cancellationPolicies.filter(
    (policy) => !UNRELEASED_CANCELLATION_POLICIES.includes(policy) || policy === currentCancellationPolicy,
  );
}

export function getFlashCancellationOptionPolicies(
  schemaPolicies: Array<string>,
  contractedCancellationPolicy?: string,
  storedPolicies: Array<string> = [],
): Array<string> {
  const wanted = new Set<string>([...FLASH_CANCELLATION_OPTION_POLICIES, ...storedPolicies]);
  if (contractedCancellationPolicy) {
    wanted.add(contractedCancellationPolicy);
  }
  return schemaPolicies.filter((policy) => wanted.has(policy));
}

export function withContractedCancellationPolicy(
  policies: Array<string> | undefined,
  contractedCancellationPolicy?: string,
): Array<string> {
  const list = (policies ?? []).filter(Boolean);
  if (list.length === 0 || !contractedCancellationPolicy || list.includes(contractedCancellationPolicy)) {
    return list;
  }
  return [contractedCancellationPolicy, ...list];
}

type PolicyType =
  | 'prior-to-check-in-one-day'
  | 'prior-to-check-in-two-days'
  | 'prior-to-check-in-three-days'
  | 'prior-to-check-in-seven-days'
  | 'prior-to-check-in-fourteen-days'
  | 'prior-to-check-in-twenty-one-days'
  | 'post-purchase-seven-days'
  | 'prior-to-check-in-twenty-eight-days'
  | 'prior-to-check-in-thirty-one-days'
  | 'prior-to-check-in-forty-five-days'
  | 'prior-to-check-in-sixty-days'
  | 'prior-to-check-in-ninety-days'
  | 'non-refundable';

export function getCancellationPolicyDaysFromPolicyType(policyType: PolicyType): number {
  return CANCELLATION_POLICY_DAYS[policyType] || 0;
}

export function buildLPCRateCancellationPolicyDisplay(item: App.OrderItem): App.CancellationPolicyDisplay {
  const { reservation } = item;
  const propertyTZOffset = item.offer_package?.property?.timezone_offset;
  const propertyTimezone = item.offer_package?.property?.timezone;

  const rateCancellationPolicy = item.offer_package?.property?.roomRate?.rate_plan?.cancellation_policy as PolicyType;
  const rateCancellationPolicyDetails = item.offer_package?.property?.roomRate?.rate_plan?.cancellation_policy_detail;

  const daysBeforeCheckIn = getCancellationPolicyDaysFromPolicyType(rateCancellationPolicy);
  const reservationCheckInDate = propertyTZOffset
    ? offsetUTC(reservation.check_in, propertyTZOffset)
    : reservation.check_in;

  const cancellationDeadline = subDays(daysBeforeCheckIn, reservationCheckInDate);
  const formattedCancellationDeadline = formatDateShortDD(cancellationDeadline);

  const deadlineInVendorTimezone = formatDate.formattedDateByTimezone(cancellationDeadline, propertyTimezone);
  const deadlineForEval = cancellationDeadline;
  const isOutsidePolicy = isBefore(deadlineForEval);

  const isNonRefundablePolicy = rateCancellationPolicy === 'non-refundable';

  return {
    isRefundable: rateCancellationPolicy !== 'non-refundable',
    deadline: formattedCancellationDeadline,
    description: rateCancellationPolicyDetails,
    title: rateCancellationPolicy,
    isOutsidePolicy: isOutsidePolicy || isNonRefundablePolicy,
    deadlineInVendorTimezone,
  };
}

/** Build LPC connection (dynamic) policy display */
export function buildLPCConnectionCancellationPolicyDisplay(
  item: App.OrderItem,
): App.CancellationPolicyDisplay | undefined {
  const { reservation } = item;
  const connectionCancellationPolicies = reservation.connection?.cancellation_policies ?? [];

  if (!connectionCancellationPolicies.length) {
    return undefined;
  }

  // A tier's `deadline` is the point at which its fee STARTS applying, not an expiry — lib-refunds
  // words it as "cancellations made after <deadline> will result in a <sell> fee". So the tiers form
  // a ladder: free until the earliest deadline, then progressively larger fees. Sort explicitly;
  // the API's ordering is not guaranteed.
  const tiers = [...connectionCancellationPolicies].sort((a, b) =>
    a.deadline === b.deadline ? 0 : a.deadline < b.deadline ? -1 : 1,
  );

  // The fee charged if we cancel right now comes from the LAST tier whose deadline has passed.
  // Undefined while the booking is still inside its free-cancellation window.
  const feeInForce = tiers.filter((policy) => policy.deadline && isBefore(policy.deadline)).pop();
  const nonRefundable = tiers.some((policy) => policy.non_refundable);

  // Free cancellation runs until the EARLIEST deadline. Previously this showed the next deadline
  // still in the future, which mid-ladder is just the point the fee steps up — so a booking that
  // stopped being free weeks ago was presented as "cancellable until" a later date.
  const freeCancellationDeadline = tiers[0].deadline || '';

  const propertyTimezone = item.offer_package?.property?.timezone;
  // `amount_percent.sell` is the fee in the order's sell currency. The sibling `amount` and
  // `currency` fields are the supplier-side cost, so `currency` must NOT be used to label `sell`.
  // `room_rate_currency` is the sell currency on the same reservation.
  const feeCurrency = reservation.room_rate_currency;
  const feeAmount = feeInForce?.amount_percent?.sell;

  return {
    isRefundable: !nonRefundable,
    deadline: freeCancellationDeadline,
    description: (feeInForce ?? tiers[tiers.length - 1]).description,
    title: nonRefundable ? 'Non-refundable' : 'Cancellation Policy',
    isOutsidePolicy: !!feeInForce || nonRefundable,
    deadlineInVendorTimezone: formatDate.formattedDateByTimezone(freeCancellationDeadline, propertyTimezone),
    freeCancellationEnded: !!feeInForce,
    cancellationFee: feeAmount != null && feeCurrency ? { amount: feeAmount, currency: feeCurrency } : undefined,
  };
}

function buildFlashCancellationPolicyDisplay(
  item: App.OrderItem,
  purchaseDate: string | Date,
): App.CancellationPolicyDisplay | undefined {
  const { reservation } = item;

  if (!reservation?.cancellation_policies?.length) {
    return undefined;
  }

  const policy = reservation.cancellation_policies[0];
  const deadline = policy.start || '';

  const propertyTimezone = item.offer_package?.property?.timezone;
  const deadlineInVendorTimezone = formatDate.formattedDateByTimezone(deadline, propertyTimezone);

  const baseOutsidePolicy = deadline ? isBefore(deadline) : false;

  const changeOfMindPolicyDeadline = getChangeOfMindPolicyDeadline(
    purchaseDate,
    reservation.check_in,
    policy.cancellation_policy,
  );
  const isWithinChangeOfMind = isWithinChangeOfMindPolicy(
    purchaseDate,
    reservation.check_in,
    policy.cancellation_policy,
  );
  return {
    isRefundable: policy.cancellation_policy !== 'non-refundable',
    deadline,
    description: policy.cancellation_policy_details,
    title: policy.cancellation_policy,
    isOutsidePolicy:
      baseOutsidePolicy ||
      policy.cancellation_policy === 'hidden-cancellation-policy' ||
      policy.cancellation_policy === 'non-refundable',
    changeOfMindPolicyDeadline,
    isWithinChangeOfMind,
    deadlineInVendorTimezone,
  };
}

export function getChangeOfMindBeforeCheckInDays(cancellationPolicy?: string): number {
  return (
    (cancellationPolicy && CHANGE_OF_MIND_BEFORE_CHECK_IN_DAYS[cancellationPolicy]) || FOURTEEN_DAYS_BEFORE_CHECK_IN
  );
}

export function isWithinChangeOfMindPolicy(
  purchaseDate: string | Date,
  checkIn: string | Date,
  cancellationPolicy?: string,
) {
  // Change of mind policy is 7 days after purchase AND 14 days before check in (10 for the flexible 10-day policy).
  // e.g. if you purchase 12 days before check in, no change of mind
  // purchase 15 days before check in, you get 1 day change of mind
  // purchase 21 days before check in, you get 7 days change of mind
  // etc
  const withinSevenDaysOfPurchase = isBefore(new Date(), addDays(7, purchaseDate));
  const checkInFarEnoughAway = isBefore(
    addDays(getChangeOfMindBeforeCheckInDays(cancellationPolicy), new Date()),
    checkIn,
  );
  return withinSevenDaysOfPurchase && checkInFarEnoughAway;
}

export function getChangeOfMindPolicyDeadline(
  purchaseDate: string | Date,
  checkIn: string | Date,
  cancellationPolicy?: string,
) {
  const sevenDaysAfterPurchase = addDays(7, purchaseDate);
  const changeOfMindCheckInDeadline = subDays(getChangeOfMindBeforeCheckInDays(cancellationPolicy), checkIn);
  const validDate = minDate(sevenDaysAfterPurchase, changeOfMindCheckInDeadline).toDate();
  return formatDateShortDD(validDate);
}

/** Extract the primary cancellation policy for an item (Flash or LPC) */
export function extractCancellationPolicyDisplay(
  purchaseDate: string | Date,
  item: App.OrderItem,
): App.CancellationPolicyDisplay {
  const { reservation } = item;

  if (!reservation) {
    return undefined;
  }

  const isLPC = [OFFER_TYPE_TACTICAL_AO_HOTEL, OFFER_TYPE_RENTAL].includes(item.offer?.type);

  if (isLPC) {
    const ignoreDynamicCancellationPolicy =
      item.offer_package?.property?.roomRate?.rate_plan?.ignore_dynamic_cancellation_policy;
    const hasConnection = !!item.reservation.connection;

    if (ignoreDynamicCancellationPolicy) {
      return buildLPCRateCancellationPolicyDisplay(item);
    }

    if (hasConnection) {
      return buildLPCConnectionCancellationPolicyDisplay(item);
    }

    if (reservation?.cancellation_policies?.length) {
      const policy = reservation.cancellation_policies[0];
      const baseOutsidePolicy = policy.start ? isBefore(policy.start) : false;
      const isWithinChangeOfMind = isWithinChangeOfMindPolicy(
        purchaseDate,
        reservation.check_in,
        policy.cancellation_policy,
      );

      const deadline = policy.start || '';
      const propertyTimezone = item.offer_package?.property?.timezone;
      const deadlineInVendorTimezone = formatDate.formattedDateByTimezone(deadline, propertyTimezone);

      return {
        isRefundable: policy.cancellation_policy !== 'non-refundable',
        deadline,
        description: policy.cancellation_policy_details,
        title: policy.cancellation_policy,
        isOutsidePolicy:
          baseOutsidePolicy ||
          policy.cancellation_policy === 'hidden-cancellation-policy' ||
          policy.cancellation_policy === 'non-refundable',
        deadlineInVendorTimezone,
      };
    }
  }

  const isFlash = item.offer?.type === OFFER_TYPE_HOTEL;
  if (isFlash) {
    return buildFlashCancellationPolicyDisplay(item, purchaseDate);
  }

  return undefined;
}
